import React, {
  KeyboardEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Popover } from "radix-ui";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { uniqueId } from "lodash";

export type TypeaheadSearchProps<T> = {
  children: ReactNode;
  externalQuery: string;
  getResults: (query: string) => Promise<T[]>;
  formatResult: (result: T) => ReactNode;
  getId: (result: T) => string;
  debounceTime: number;
  onSelect?: (result: T) => void;
  disabled?: boolean;
  contentClassName?: string;
};

// The suggestions live in a non-modal Popover anchored to the caller's input,
// so focus and keystrokes stay in that input while the list is open. A Radix
// Select can't be used here: its content traps focus and swallows typing.
export function TypeaheadSearch<T>({
  children,
  externalQuery,
  getResults,
  formatResult,
  getId,
  debounceTime,
  onSelect,
  disabled = false,
  contentClassName,
}: TypeaheadSearchProps<T>) {
  const [results, setResults] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listboxId] = useState(() => uniqueId("search-listbox-"));
  const anchorRef = useRef<HTMLDivElement>(null);
  const latestRequestID = useRef(0);

  useEffect(() => {
    const requestID = ++latestRequestID.current;
    const timeout = window.setTimeout(() => {
      void getResults(externalQuery)
        .then((rows) => {
          if (requestID !== latestRequestID.current) return;
          setResults(rows);
          setActiveIndex(-1);
        })
        .catch((error) => {
          if (requestID !== latestRequestID.current) return;
          console.error(error);
          setResults([]);
          setActiveIndex(-1);
        });
    }, debounceTime);
    return () => window.clearTimeout(timeout);
  }, [debounceTime, externalQuery, getResults]);

  const visible = open && !disabled && results.length > 0;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  useEffect(() => {
    if (!visible || activeIndex < 0) return;
    document
      .getElementById(`${listboxId}-option-${activeIndex}`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, listboxId, visible]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const select = useCallback(
    (result: T) => {
      onSelect?.(result);
      close();
    },
    [close, onSelect],
  );

  // Capture phase, so a handled key never reaches the caller's input
  // handlers. An Enter with no highlighted suggestion passes through, which
  // lets free-text callers commit what was typed.
  const handleKeyDownCapture = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    let handled = true;
    if (!visible) {
      if (e.key === "ArrowDown" && results.length > 0) {
        setOpen(true);
      } else {
        handled = false;
      }
    } else if (e.key === "ArrowDown") {
      setActiveIndex((i) => (i + 1 >= results.length ? 0 : i + 1));
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      select(results[activeIndex]);
    } else if (e.key === "Escape") {
      close();
    } else {
      handled = false;
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement, {
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-expanded": visible,
        "aria-controls": visible ? listboxId : undefined,
        "aria-activedescendant":
          visible && activeIndex >= 0 ? optionId(activeIndex) : undefined,
      })
    : children;

  return (
    <Popover.Root
      open={visible}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <Popover.Anchor asChild>
        <div
          ref={anchorRef}
          className="w-full"
          onChange={() => {
            setOpen(true);
            setActiveIndex(-1);
          }}
          onMouseDown={() => setOpen(true)}
          onKeyDownCapture={handleKeyDownCapture}
        >
          {trigger}
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (anchorRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
          className={twMerge(
            clsx(
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut bg-surface-default z-popover shadow-elevation-200 max-h-96 w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-lg px-3 py-2",
              contentClassName,
            ),
          )}
        >
          <div id={listboxId} role="listbox">
            {results.map((result, index) => (
              <div
                key={getId(result)}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className={clsx(
                  "text-textPrimary cursor-pointer rounded-lg px-4 py-1.5",
                  index === activeIndex && "bg-surface-hover",
                )}
                // Keeps focus in the input, so the click selects instead of
                // first blurring the input and dismissing the list.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(result)}
              >
                {formatResult(result)}
              </div>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
